import * as React from 'react';
import { Box, Collapse } from '@mui/material';
import {
    DashboardMenuItem,
    MenuItemLink,
    MenuProps,
    useSidebarState,
} from 'react-admin';
import clsx from 'clsx';
import ChatIcon from '@mui/icons-material/Chat';
import GroupsIcon from '@mui/icons-material/Groups';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';

const SCROLL_TOP_STATE = { _scrollToTop: true };

const USER_MANAGEMENT_ITEMS: Array<{
    to: string;
    primaryText: string;
    leftIcon: React.ReactNode;
}> = [
    {
        to: '/admin/user-management/player-users',
        primaryText: 'Players',
        leftIcon: <SportsSoccerIcon />,
    },
    {
        to: '/admin/user-management/club-users',
        primaryText: 'Clubs',
        leftIcon: <GroupsIcon />,
    },
    {
        to: '/admin/user-management/coach-users',
        primaryText: 'Coaches',
        leftIcon: <SupportAgentIcon />,
    },
    {
        to: '/admin/user-management/admin-users',
        primaryText: 'Admins',
        leftIcon: <SupervisorAccountIcon />,
    },
];

const Menu = ({ dense = false }: MenuProps) => {
    const [open] = useSidebarState();
    const location = useLocation();
    const isInUserManagement = location.pathname.startsWith('/admin/user-management/');
    const [usersManagementOpen, setUsersManagementOpen] = React.useState(isInUserManagement);

    React.useEffect(() => {
        if (isInUserManagement) {
            setUsersManagementOpen(true);
        }
    }, [isInUserManagement]);

    const handleToggleUsersManagement = React.useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        setUsersManagementOpen(prev => !prev);
    }, []);

    return (
        <Box
            sx={{
                width: open ? 200 : 50,
                marginTop: 1,
                marginBottom: 1,
                transition: theme =>
                    theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
            }}
            className={clsx({
                'RaMenu-open': open,
                'RaMenu-closed': !open,
            })}
        >
            <DashboardMenuItem />
            <MenuItemLink
                to="/admin/chats"
                state={SCROLL_TOP_STATE}
                primaryText="Chats"
                leftIcon={<ChatIcon />}
                dense={dense}
            />
            <MenuItemLink
                to={location.pathname}
                onClick={handleToggleUsersManagement}
                primaryText="Users Management"
                leftIcon={<ManageAccountsIcon />}
                rightIcon={usersManagementOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                aria-expanded={usersManagementOpen}
                dense={dense}
            />
            <Collapse in={usersManagementOpen} timeout="auto" unmountOnExit>
                <Box sx={{ pl: open ? 2 : 0 }}>
                    {USER_MANAGEMENT_ITEMS.map(item => (
                        <MenuItemLink
                            key={item.to}
                            to={item.to}
                            state={SCROLL_TOP_STATE}
                            primaryText={item.primaryText}
                            leftIcon={item.leftIcon}
                            dense={dense}
                        />
                    ))}
                </Box>
            </Collapse>
            <MenuItemLink
                to="/admin/aiVideo"
                state={SCROLL_TOP_STATE}
                primaryText="AI Videos"
                leftIcon={<VideoLibraryIcon />}
                dense={dense}
            />
            <MenuItemLink
                to="/admin/assessment"
                state={SCROLL_TOP_STATE}
                primaryText="Assessment"
                leftIcon={<AssessmentIcon />}
                dense={dense}
            />
            <MenuItemLink
                to="/admin/ai-reports"
                state={SCROLL_TOP_STATE}
                primaryText="AI Reports"
                leftIcon={<ReportProblemIcon />}
                dense={dense}
            />
            <MenuItemLink
                to="/admin/kivoai-videos"
                state={SCROLL_TOP_STATE}
                primaryText="KivoAI Videos"
                leftIcon={<SmartDisplayIcon />}
                dense={dense}
            />
            <MenuItemLink
                to="/admin/user-subscriptions"
                state={SCROLL_TOP_STATE}
                primaryText="User Subscriptions"
                leftIcon={<WorkspacePremiumIcon />}
                dense={dense}
            />
        </Box>
    );
};

export default Menu;
